import { useCallback, useState } from "react";
import {
  saveAccountDefaultTemplate,
  useAccountDefaultTemplate,
} from "@/lib/account-default-template";
import {
  readFavorites,
  toggleFavorite,
  useTemplatePreferenceVersion,
} from "@/lib/template-preferences";
import { useTemplateList } from "@/lib/template-queries";
import { useVisitScope } from "@/lib/visit-queries";

// Favourites and default for the signed-in account, with the writes that
// change them. `setDefault` resolves to false when the server refused, in
// which case nothing changed.
export function useTemplatePreferences() {
  useTemplatePreferenceVersion();
  const scope = useVisitScope();
  const account = scope?.accountPublicId;
  const { templates, loading, ready, error } = useTemplateList();
  const defaultTemplate = useAccountDefaultTemplate();
  const [saving, setSaving] = useState<string | null>(null);

  const setDefault = useCallback(
    async (value: string): Promise<boolean> => {
      if (!scope) return false;
      setSaving(value);
      try {
        return await saveAccountDefaultTemplate(scope, value);
      } finally {
        setSaving(null);
      }
    },
    [scope],
  );

  return {
    account,
    templates,
    loading: loading || defaultTemplate.loading,
    ready: ready && !defaultTemplate.loading,
    error: error || defaultTemplate.error,
    defaultTemplate: defaultTemplate.value,
    favorites: readFavorites(account),
    isFavorite: (value: string) => readFavorites(account).includes(value),
    toggleFavorite: (value: string) => {
      toggleFavorite(account, value);
    },
    setDefault,
    saving,
  };
}
